import { IconApplication } from 'assets/icons';
import { AxiosError, AxiosResponse } from 'axios';
import { FilterBlock } from 'components/FiltersBlock';
import { useAppSelector } from 'hooks/useAppSelector';
import { NoneOrders } from 'pages/Orders/NoneOrders';
import { useEffect } from 'react';
import { Range } from 'react-date-range';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { ordersActions } from 'reduxStore/reducers/OrdersSlice';
import { getInvitationPageFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getInvitationPageFilters';
import { getPermissions } from 'reduxStore/reducers/ProfileSlice/selectors/getPermissions';
import { InvitationService } from 'services/InvitationService';
import { Contracts, InvitationResendRequestDto } from 'services/InvitationService/types';
import { BaseError } from 'types';
import { Button } from 'ui/redesign/Button';
import { Table } from 'ui/redesign/Table';
import { TableCard } from 'ui/redesign/Table/TableCard';
import { TableLoading } from 'ui/redesign/Table/TableLoading';
import { alertBaseError, prepareFilterData } from 'utils/helpers';
import { determinateInvitationFiltersCount } from '../utils';
import { ClientInviteModal } from './ClientInviteModal';
import { GenerateInvitationTableHeadings } from './const';
import { InvitationFiltersModal } from './InvitationFiltersForm';
import { InvitationFiltersFormValues } from './InvitationFiltersForm/types';
import { MultiValue, SingleValue } from 'react-select';
import { OptionType } from 'ui/redesign/Select/types';

export const Invites = () => {
  const dispatch = useDispatch();
  const filters = useAppSelector(getInvitationPageFilters);
  const permissions = useAppSelector(getPermissions);

  const {
    mutateAsync: getInvitationList,
    isLoading,
    data,
  } = useMutation<
    AxiosResponse<Contracts>,
    AxiosError<BaseError>,
    {
      createDate: Range | null;
      fullName: string | null;
      email: string | null;
      statuses: number[] | null;
    }
  >({
    mutationKey: 'getInvitationCooperateList',
    mutationFn: ({ createDate, fullName, email, statuses }) => {
      return InvitationService.getInvitationCooperateList({
        startTime: createDate?.startDate?.toISOString() || null,
        endTime: createDate?.endDate?.toISOString() || null,
        fullName,
        email,
        statuses,
        skipCount: 0,
        maxResultCount: 999,
      });
    },
    onError: (err: AxiosError<BaseError>) => {
      alertBaseError(err);
    },
  });

  const { mutateAsync: resendInvite } = useMutation<
    AxiosResponse<void>,
    AxiosError<BaseError>,
    InvitationResendRequestDto
  >({
    mutationKey: 'resendInvite',
    mutationFn: ({ id, concurrencyStamp }) =>
      InvitationService.resendInvite({
        id,
        concurrencyStamp,
      }),
    onSuccess: () => {
      toast.success('Повторное приглашение передано в отправку');
      const statusesValues = filters.statuses
        ? prepareFilterData(
            filters.statuses as unknown as MultiValue<OptionType> | SingleValue<OptionType>
          )
        : null;

      const filtersBody = {
        createDate: filters.createDate,
        fullName: filters.fullName,
        email: filters.email,
        statuses: statusesValues,
      };
      getInvitationList(filtersBody);
    },
    onError: (err: AxiosError<BaseError>) => {
      alertBaseError(err);
    },
  });

  const handleOnFilter = (newFilters: InvitationFiltersFormValues) => {
    const statusesValues = newFilters.statuses
      ? prepareFilterData(
          newFilters.statuses as unknown as MultiValue<OptionType> | SingleValue<OptionType>
        )
      : null;

    const filtersBody = {
      createDate: newFilters.createDate,
      fullName: newFilters.fullName,
      email: newFilters.email,
      statuses: statusesValues,
    };
    dispatch(ordersActions.setInvitationPageFilters(newFilters));
    getInvitationList(filtersBody);
  };

  const handleOpenFilterModal = (): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: <InvitationFiltersModal onFilter={handleOnFilter} />,
        options: {
          withCloseButton: false,
          modalClassName: 'w-full sm:w-[650px] !overflow-visible',
          contentClassName: '!overflow-visible',
        },
      })
    );
  };

  const inviteModal = (): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: <ClientInviteModal refetch={getInvitationList} />,
        options: {
          modalClassName: 'w-full sm:w-[650px] !overflow-visible',
          contentClassName: '!overflow-visible',
          title: 'Создание приглашения',
        },
      })
    );
  };

  useEffect(() => {
    const statuses = filters.statuses
      ? prepareFilterData(
          filters.statuses as unknown as MultiValue<OptionType> | SingleValue<OptionType>
        )
      : null;

    getInvitationList({
      createDate: filters.createDate,
      fullName: filters.fullName,
      email: filters.email,
      statuses,
    });
  }, []);

  return (
    <section className="mt-6 flex flex-col gap-6">
      <Button
        onClick={inviteModal}
        className="absolute right-9 top-9 h-[42px] py-3 px-6 text-sm uppercase"
      >
        ПРИГЛАСИТЬ
      </Button>
      <FilterBlock
        countFilters={determinateInvitationFiltersCount(filters)}
        onFilterButtonClick={handleOpenFilterModal}
      />
      {isLoading ? (
        <TableLoading />
      ) : (
        <>
          {data?.data.totalCount === 0 ? (
            <div className="min-h-[90vh] overflow-hidden rounded-[10px] bg-base p-8 shadow-3xl">
              <NoneOrders
                icon={<IconApplication />}
                heading={
                  determinateInvitationFiltersCount(filters)
                    ? 'Приглашения не найдены'
                    : 'Приглашений нет'
                }
                description={
                  determinateInvitationFiltersCount(filters)
                    ? 'Измените настройки фильтра и попробуйте еще раз'
                    : 'Как только Приглашения появятся, они будут отображаться в данном разделе.'
                }
              />
            </div>
          ) : (
            <TableCard>
              <Table
                tableHeading={GenerateInvitationTableHeadings({ permissions, resendInvite })}
                tableData={(data?.data.items || []) as unknown as TableData[]}
              />
            </TableCard>
          )}
        </>
      )}
    </section>
  );
};
